import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Mail, Phone, ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen py-8 sm:py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#9A3412] mb-4">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-2xl gradient-orange grid place-items-center shadow-md">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Happy Journey 4U</div>
            <div className="text-xl font-bold">HJ4U Campaigns</div>
          </div>
        </div>

        <div className="glass-card p-6 sm:p-8 space-y-5">
          <header>
            <h1 className="text-2xl sm:text-3xl font-bold">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground mt-1">Last updated: 11 May 2026</p>
          </header>

          <Section title="1. Who we are">
            HJ4U Campaigns (the “App”) is an internal WhatsApp marketing tool operated by <strong>Happy Journey 4U</strong>, a travel agency based in India. The App is used by Happy Journey 4U staff to manage customer contacts, design WhatsApp message templates and send approved marketing or service campaigns to customers who have an existing relationship with the agency.
          </Section>

          <Section title="2. Information we collect">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Customer contact details</strong> uploaded by Happy Journey 4U staff: name, phone number, email (optional), tags and notes.</li>
              <li><strong>WhatsApp conversation data</strong> sent or received through the Meta WhatsApp Business Cloud API on the Happy Journey 4U business number, including message text, media files (images, videos, PDFs, voice notes), timestamps and delivery / read status.</li>
              <li><strong>Account data</strong> for staff who sign in to the App: email address and a hashed password.</li>
              <li><strong>Technical data</strong> such as IP address, device type and browser logs that our hosting provider may collect for security and operations.</li>
            </ul>
            <p>We do <strong>not</strong> sell or rent personal data. We do not run advertising networks on this App.</p>
          </Section>

          <Section title="3. How we use the information">
            <ul className="list-disc pl-5 space-y-1">
              <li>To send WhatsApp marketing, transactional and service messages to customers who have shared their number with Happy Journey 4U.</li>
              <li>To manage two-way conversations between staff and customers within Meta’s 24-hour customer service window.</li>
              <li>To submit message templates to Meta for approval and to record their status.</li>
              <li>To track delivery, read and failure status of campaign messages.</li>
              <li>To secure, troubleshoot and improve the App.</li>
            </ul>
          </Section>

          <Section title="4. WhatsApp Business Platform">
            All WhatsApp messages are delivered through the Meta WhatsApp Business Cloud API. By using WhatsApp, the customer also accepts the WhatsApp Terms of Service and Privacy Policy. We follow Meta’s WhatsApp Business Messaging Policy, including:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>Sending marketing templates only to numbers that have an existing relationship with our agency.</li>
              <li>Using approved templates outside the 24-hour customer service window.</li>
              <li>Honouring opt-out requests when a customer asks to stop receiving messages.</li>
            </ul>
          </Section>

          <Section title="5. Sharing with third parties">
            We only share data with the service providers that make this App work:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><strong>Meta Platforms, Inc.</strong> — to deliver WhatsApp messages.</li>
              <li><strong>Google LLC (Gemini API)</strong> — to help draft message template content. Only the template description text you enter is sent. Customer phone numbers are never shared with Gemini.</li>
              <li><strong>Supabase Inc.</strong> — our database and storage provider.</li>
            </ul>
            We never share customer data for any other party’s advertising or marketing.
          </Section>

          <Section title="6. Data retention">
            Contact records and conversation history are kept for as long as Happy Journey 4U needs them to provide travel services and marketing follow-ups, or until the customer asks us to delete them. Webhook logs are retained for up to 90 days for troubleshooting.
          </Section>

          <Section title="7. Your rights">
            You may, at any time, ask us to:
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>See what data we hold about you.</li>
              <li>Correct or update your contact details.</li>
              <li>Remove your number from our database and stop further marketing messages.</li>
              <li>Receive a copy of your data in a portable format.</li>
            </ul>
            To exercise any of these rights, contact us at the email below. You can also stop WhatsApp messages instantly by replying <em>STOP</em> on the same conversation.
          </Section>

          <Section title="8. Security">
            Passwords are stored as bcrypt hashes. API calls use HTTPS and bearer-token authentication. Access to the dashboard is restricted to Happy Journey 4U staff.
          </Section>

          <Section title="9. Children’s privacy">
            The App is not directed to children under 13 and we do not knowingly collect data from them.
          </Section>

          <Section title="10. Changes to this policy">
            We may update this Privacy Policy from time to time. The “Last updated” date at the top will reflect the latest version. Significant changes will be communicated through the App or by WhatsApp.
          </Section>

          <Section title="11. Contact us">
            <div className="space-y-1">
              <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#9A3412]" /> <a href="mailto:support@happyjourney4u.co.in" className="underline">support@happyjourney4u.co.in</a></div>
              <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#9A3412]" /> +91 83197 29163 (WhatsApp Business)</div>
              <div>Happy Journey 4U — India</div>
            </div>
          </Section>
        </div>

        <div className="text-center text-xs text-muted-foreground mt-6">
          <Link to="/terms" className="underline">Terms of Service</Link>
          <span className="mx-2">·</span>
          <Link to="/login" className="underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg sm:text-xl font-semibold mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-[hsl(var(--foreground))]/85 space-y-2">{children}</div>
    </section>
  );
}
